// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { Link, H5, H4 } from '@codex/basics';
import styled from 'styled-components';
import { Icon } from '@codex/icons';

const Wrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.shades[5]};
  border-width: 1px 0;
  display: flex;
  padding-top: 15px;

  @media ${({ theme }) => `${theme.breakpoints.desktop}`} {
    padding-left: 10px;
  }

  @media ${({ theme }) => `${theme.breakpoints.tabletMin}, ${theme.breakpoints.desktop}`} {
    padding-top: 0;
  }
`;

const Navigation = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;

  @media ${({ theme }) => `${theme.breakpoints.tabletMin}, ${theme.breakpoints.desktop}`} {
    flex-direction: row;
  }
`;

const NavigationList = styled.ul`
  flex: 1;
  list-style: none;
  padding: 0;
  margin: 0;

  @media ${({ theme }) => `${theme.breakpoints.tabletMin}`} {
    padding: 15px;
    margin-bottom: 15px;
  }

  @media ${({ theme }) => `${theme.breakpoints.desktop}`} {
    padding: 5px 0 0;
  }
`;

const NavigationItem = styled.li`
  display: none;
  padding-bottom: 3px;

  &:nth-child(2),
  &:nth-child(3) {
    display: block;
  }

  @media ${({ theme }) => `${theme.breakpoints.tabletMin}, ${theme.breakpoints.desktop}`} {
    display: block;
    padding-bottom: 0;
  }
`;

const NavigationHeading = styled(H5)`
  margin-bottom: 7px;
  font-size: 14px;

  @media ${({ theme }) => `${theme.breakpoints.desktop}`} {
    font-size: 13px;
  }
`;

const NavigationLink = styled(Link)`
  color: inherit;
  margin: 5px;
  padding: 8px;
  display: block;

  &:hover {
    color: ${({ theme }) => theme.colors.link.default};
    text-decoration: none;
  }

  @media ${({ theme }) => `${theme.breakpoints.tabletMin}`} {
    display: inline-block;
    margin: 0 0 3.5px;
    padding: 5px 5px 5px 0;
  }

  @media ${({ theme }) => `${theme.breakpoints.desktop}`} {
    display: inline-block;
    margin: 0 0 3.5px;
    padding: 2px 2px 2px 0;
  }

  @media ${({ theme }) => `${theme.breakpoints.desktop}`} {
    &:hover {
      text-decoration: underline;
    }
  }
`;

const StoreFinder = styled.div`
  position: relative;
  display: none;

  @media ${({ theme }) => `${theme.breakpoints.desktop}`} {
    width: 26%;
    display: inline-block;
    border-left: 1px solid ${({ theme }) => theme.colors.shades[5]};
    padding: 5px 10px 10px;
    background: #fbfbfb;
  }
`;

const StoreFinderHeading = styled(H4)`
  margin-bottom: 6.5px;
`;

const StoreFinderDescription = styled.p`
  margin-bottom: 13px;
  font-size: 13px;
  width: 65%;
  line-height: 1.4em;
`;

const StorefinderIcon = styled(Icon)`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
`;

const StoreFinderLink = styled(Link)`
  position: relative;
  display: inline-block;
  padding: 12px 10px 12px 26px;
  font-size: 12px;
  color: inherit;
  background-color: #ebebeb;

  &:hover {
    color: ${({ theme }) => theme.colors.link.default};

    ${StorefinderIcon} {
      color: ${({ theme }) => theme.colors.link.default};
    }
  }
`;

const StoreFinderMapWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 75px;
  height: 120px;
  opacity: 0.5;
  overflow: hidden;
`;

const dataIsReady = data => data && data.nav && !!data.nav.length;

const FooterNavigation = ({ StoreFinderMap, ...props }) => (
  <Wrapper>
    <Navigation>
      {dataIsReady(props.data) &&
          props.data.nav.map((section, i) => (
            <NavigationList key={i}>
              <NavigationItem>
                <NavigationHeading>{section.label}</NavigationHeading>
              </NavigationItem>
              {section.nav.map((item, j) => (
                <NavigationItem key={j}>
                  <NavigationLink
                    href={item.link}
                    title={item.label.replace(/<[^>]+>/g, '')}
                  >
                    {item.label && (
                      <div dangerouslySetInnerHTML={{ __html: item.label }} />
                    )}
                  </NavigationLink>
                </NavigationItem>
              ))}
            </NavigationList>
          ))}
    </Navigation>
    <StoreFinder>
      <StoreFinderHeading>{props.storeFinderTitle}</StoreFinderHeading>
      <StoreFinderDescription>
        {props.storeFinderDescription}
      </StoreFinderDescription>
      <StoreFinderLink
        href={props.storeFinderUrl}
        title={props.storeFinderLinkText}
      >
        <StorefinderIcon icon={props.storeFinderLinkIcon} />
        {props.storeFinderLinkText}
      </StoreFinderLink>
      <StoreFinderMapWrapper>
        <StoreFinderMap />
      </StoreFinderMapWrapper>
    </StoreFinder>
  </Wrapper>
);
FooterNavigation.propTypes = {
  data: PropTypes.shape({
    nav: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string,
        label: PropTypes.string,
        style: PropTypes.string,
        link: PropTypes.string,
        nav: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            link: PropTypes.string,
            nav: PropTypes.arrayOf(
              PropTypes.shape({
                label: PropTypes.string,
                link: PropTypes.string,
                nav: PropTypes.arrayOf(PropTypes.any)
              })
            )
          })
        )
      })
    )
  }),
  /**
   * Map component
   */
  StoreFinderMap: PropTypes.func.isRequired,
  /**
   * Title of store finder section of footer
   */
  storeFinderTitle: PropTypes.string.isRequired,
  /**
   * Description displayed in store finder section of footer
   */
  storeFinderDescription: PropTypes.string.isRequired,
  /**
   * Link to store finder application. Defaults to production.
   */
  storeFinderUrl: PropTypes.string.isRequired,
  /**
   * Text displayed in the "Find stores" button
   */
  storeFinderLinkText: PropTypes.string.isRequired,
  /**
   * Icon displayed in the "Find stores" button
   */
  storeFinderLinkIcon: PropTypes.string.isRequired
};

FooterNavigation.defaultProps = {
  data: undefined
};

/** @component */
export default FooterNavigation;
